/**
 * Wave Rule Service - 业务逻辑层 + Mock 适配
 * 页面组件只调用此 Service 层,不直接调用 API
 */

import { config } from '../../config';
import * as waveMock from '../../mocks/wave';
import httpClient from '../httpClient';
import { API_ENDPOINTS } from '../../utils/apiEndpoints';
import type { ApiResponse, PageData } from '../../types/common';
import type {
  WaveRuleDTO,
  QueryWaveRulePageRequest,
  CreateWaveRuleRequest,
  UpdateWaveRuleRequest,
} from '../../types/wave/waveRule';
import type {
  WaveInstanceDTO,
  TriggerWaveRequest,
} from '../../types/wave/waveInstance';

/**
 * 分页查询波次规则
 */
export async function pageWaveRules(
  params: QueryWaveRulePageRequest
): Promise<ApiResponse<PageData<WaveRuleDTO>>> {
  if (config.mock) {
    return waveMock.mockPageWaveRules(params);
  }

  return httpClient.post(API_ENDPOINTS.WAVE_RULE.PAGE, params);
}

/**
 * 按编码查询波次规则
 */
export async function getWaveRuleByCode(
  ruleCode: string
): Promise<ApiResponse<WaveRuleDTO>> {
  if (config.mock) {
    return waveMock.mockGetWaveRuleByCode(ruleCode);
  }

  return httpClient.get(`${API_ENDPOINTS.WAVE_RULE.BASE}/${ruleCode}`);
}

/**
 * 创建波次规则
 */
export async function createWaveRule(
  data: CreateWaveRuleRequest
): Promise<ApiResponse<WaveRuleDTO>> {
  if (config.mock) {
    return waveMock.mockCreateWaveRule(data);
  }

  return httpClient.post(API_ENDPOINTS.WAVE_RULE.CREATE, data);
}

/**
 * 更新波次规则
 */
export async function updateWaveRule(
  data: UpdateWaveRuleRequest
): Promise<ApiResponse<WaveRuleDTO>> {
  if (config.mock) {
    return waveMock.mockUpdateWaveRule(data);
  }

  return httpClient.post(API_ENDPOINTS.WAVE_RULE.UPDATE, data);
}

/**
 * 启用波次规则
 */
export async function enableWaveRule(
  ruleCode: string
): Promise<ApiResponse<WaveRuleDTO>> {
  if (config.mock) {
    return waveMock.mockEnableWaveRule(ruleCode);
  }

  return httpClient.post(API_ENDPOINTS.WAVE_RULE.ENABLE, { ruleCode });
}

/**
 * 禁用波次规则
 */
export async function disableWaveRule(
  ruleCode: string
): Promise<ApiResponse<WaveRuleDTO>> {
  if (config.mock) {
    return waveMock.mockDisableWaveRule(ruleCode);
  }

  return httpClient.post(API_ENDPOINTS.WAVE_RULE.DISABLE, { ruleCode });
}

/**
 * 删除波次规则
 */
export async function deleteWaveRules(
  ruleCodes: string[]
): Promise<ApiResponse<null>> {
  if (config.mock) {
    return waveMock.mockDeleteWaveRules(ruleCodes);
  }

  return httpClient.post(API_ENDPOINTS.WAVE_RULE.DELETE, { ruleCodes });
}

/**
 * 获取渠道（Channel）下拉选项 - 复用订单模块的全局渠道字典接口（不限仓库，确保波次规则可选择所有渠道，而非仅当前仓库已有订单的渠道）
 */
export async function getChannelOptions(): Promise<ApiResponse<Array<{ value: string; label: string }>>> {
  if (config.mock) {
    return waveMock.mockGetChannelOptions();
  }

  return httpClient.post(API_ENDPOINTS.ORDER.OPTION_GLOBAL);
}

/**
 * 手动触发波次
 */
export async function triggerWave(
  data: TriggerWaveRequest
): Promise<ApiResponse<WaveInstanceDTO>> {
  if (config.mock) {
    return waveMock.mockTriggerWave(data);
  }

  return httpClient.post(API_ENDPOINTS.WAVE_INSTANCE.TRIGGER, data);
}
